import { Calendar, MapPin, Phone, Mail, Trash2, BadgeCheck, RotateCcw, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Spinner } from '@/shared/ui/Spinner'
import { cn, DUMMY_AVATAR, onAvatarError, pickTranslation, localized } from '@/shared/utils'
import i18n from '@/i18n'

function formatDateTime(value?: string | null) {
  if (!value) return '—'
  // The API sends "2026-09-20 22:42:53" — a space, not the "T" ISO parsing
  // needs — so `new Date(value)` as-is returns Invalid Date in Safari/Firefox.
  const d = new Date(String(value).replace(' ', 'T'))
  if (isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('en-GB').replace(/\//g,'-')} ${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',hour12:true})}`
}

// get-school-trainer-list returns trainer-mapping rows with identity nested under
// `user` and stats under different field names. Flatten/remap so TrainerCard
// (which reads the flat admin-list shape) renders the same regardless of source page.
export function mapSchoolTrainerRow(t: any) {
  const u = t.user ?? {}
  const vendorName = pickTranslation(t.vendor?.vendor_translations, i18n.language)?.name
  return {
    // identity comes from the nested user object
    ...u,
    // user id drives navigation + delete; preserve the mapping id for verify
    id: u.id ?? t.user_id ?? t.id,
    vendor_trainer_mapping_id: t.vendor_trainer_mapping_id ?? t.mapping_id ?? t.id,
    created_at: u.created_at ?? t.created_at,
    deleted_at: u.deleted_at ?? t.deleted_at,
    // trainer-mapping fields (live on the row, not on `user`)
    vehicle_number: t.vehicle_number ?? u.vehicle_number,
    vehicle_type:   t.vehicle_type ?? u.vehicle_type,
    gear:           t.gear ?? u.gear ?? u.user_preference?.gear,
    school_name:    t.school_name ?? vendorName ?? u.school_name ?? null,
    rating:         t.trainer_rating ?? u.average_rating ?? u.rating,
    total_active_students: t.total_students ?? u.total_active_students ?? 0,
    total_bookings: t.total_bookings ?? u.total_bookings ?? 0,
    // get-school-trainer-list has no revenue/earning fields — show real cash figures instead
    cash_in_hand:   t.cash_in_hand ?? u.cash_in_hand ?? 0,
    pending_online_payment: t.pending_online_payment ?? u.pending_online_payment ?? 0,
    // verification + status
    verified_at:  t.verified_at ?? u.verified_at,
    is_verified:  !!(t.verified_at ?? u.is_verified ?? u.verified_at),
    is_active:    u.is_active,
    is_suspended: u.is_suspended,
  }
}

// ── Trainer Card — shared between the admin/school trainer list and the "My School" tab ──
export function TrainerCard({ user, onView, onDelete, onToggleVerify, onRestore, deleted, restoring, deleting, verifying, isSchool, selectable, selected, onToggleSelect }: {
  user: any; onView: () => void; onDelete?: () => void; onToggleVerify?: (verify: boolean) => void
  onRestore?: () => void; deleted?: boolean; restoring?: boolean
  deleting?: boolean; verifying?: boolean; isSchool?: boolean
  // Bulk-select mode (e.g. picking trainers to send a notification to) — an
  // overlay checkbox independent of the card's own View/Delete/Verify actions.
  selectable?: boolean; selected?: boolean; onToggleSelect?: () => void
}) {
  const { t, i18n } = useTranslation()

  // "Vendor" = school assignment: school_name / school_verified_at / school_verified_by.
  // All three null means the trainer was never assigned to a school — the account
  // never made it past sign-up, so there's nothing worth showing as trainer
  // stats/vehicle/school, all of which would just render as dashes. A short,
  // dedicated card flags it for follow-up instead.
  const isIncomplete = !deleted && !user.school_name && !user.school_verified_at && !user.school_verified_by
  if (isIncomplete) {
    const name = localized(user, 'name', i18n.language)
    return (
      <div className={cn('relative bg-white rounded-2xl border-2 shadow-sm overflow-hidden',
        selectable && selected ? 'ring-2 ring-brand-500 border-brand-500' : 'border-amber-200')}>
        {selectable && (
          <button type="button" onClick={onToggleSelect}
            aria-label={selected ? 'Deselect trainer' : 'Select trainer'}
            className={cn('absolute top-3 start-3 z-10 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors shadow-sm',
              selected ? 'bg-brand-500 border-brand-500 text-white' : 'bg-white/95 border-gray-300 hover:border-brand-400')}>
            {selected && <BadgeCheck size={12} strokeWidth={3} />}
          </button>
        )}
        <div className="p-4 flex items-start justify-between gap-3" style={{ background: '#fffbeb' }}>
          <div className={cn('flex items-center gap-3 min-w-0', selectable && 'ps-6')}>
            <img src={user.photo_url || DUMMY_AVATAR} alt={name || 'T'} onError={onAvatarError}
              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100"/>
            <div className="min-w-0">
              <div className="font-bold text-gray-900 text-sm leading-tight truncate">{name || t('trainerCard.unnamedTrainer')}</div>
              <span className="text-xs text-gray-400">{t('trainerCard.idPrefix')}{user.id?.toString().padStart(4,'0')}</span>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 flex-shrink-0">
            <AlertTriangle size={11}/> {t('trainerCard.incompleteBadge')}
          </span>
        </div>

        <div className="px-4 py-3 space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Phone size={11} className="flex-shrink-0 text-gray-400"/>
            <span>{user.phone ? `+${user.country_code || '971'} ${user.phone}` : '—'}</span>
          </div>
          {!!user.email && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Mail size={11} className="flex-shrink-0 text-gray-400"/>
              <span className="truncate">{user.email}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar size={11} className="flex-shrink-0 text-gray-400"/>
            <span>{t('trainerCard.joined')}: {formatDateTime(user.created_at)}</span>
          </div>
        </div>

        <div className="mx-4 mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
          <AlertTriangle size={15} className="text-amber-500 flex-shrink-0 mt-0.5"/>
          <p className="text-xs text-amber-800 leading-relaxed">{t('trainerCard.incompleteMessage')}</p>
        </div>

        <div className="px-4 pb-4 flex items-center gap-2">
          <button
            onClick={onView}
            className="flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all hover:opacity-80"
            style={{ borderColor: '#f68b2c', color: '#f68b2c' }}
          >
            ● {t('trainerCard.viewDetails')}
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              disabled={deleting}
              title={t('trainerCard.deleteTitle')}
              className="flex items-center justify-center py-2 px-3 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {deleting ? <Spinner className="w-4 h-4"/> : <Trash2 size={16}/>}
            </button>
          )}
        </div>
      </div>
    )
  }

  // API marks a verified trainer via `verified_at` (timestamp); some payloads also expose `is_verified`.
  const isVerified = !!user.is_verified || !!user.verified_at
  // Status — API returns is_suspended and is_active
  const trainerStatus = user.is_suspended
    ? 'Suspended'
    : user.is_active ? 'Active' : 'Inactive'
  const statusLabel = user.is_suspended
    ? t('trainerCard.suspended')
    : user.is_active ? t('common.active') : t('common.inactive')

  // Vehicle — API: vehicle_type.translations[] with locale "En"/"Ar"
  const vehicleTr = pickTranslation(user.vehicle_type?.translations, i18n.language)
  const vehicle = vehicleTr?.name ?? '—'

  // Gear — API returns a flat enum string (e.g. "Automatic"); no per-locale field,
  // so map the known finite set of values through i18n instead.
  const GEAR_LABEL_KEYS: Record<string, string> = {
    automatic: 'trainerCard.gearAutomatic',
    manual:    'trainerCard.gearManual',
    both:      'trainerCard.gearBoth',
  }
  const rawGear = user.gear ?? ''
  const gearKey = GEAR_LABEL_KEYS[String(rawGear).toLowerCase()]
  const gear = (rawGear === 'Unknown' || rawGear === '' || rawGear == null)
    ? '—'
    : (gearKey ? t(gearKey) : rawGear)

  // Plate — API: vehicle_number
  const plateNo = user.vehicle_number ?? '—'

  // School — API: school_name (flat string)
  const school = user.school_name ?? '—'

  // City — API: city.city_translations[] or city.name
  const cityName = pickTranslation(user.city?.city_translations, i18n.language)?.name ?? user.city?.name ?? '—'

  // Trainer's own display name — DB carries a name_ar sibling; prefer it in Arabic UI.
  const trainerName = localized(user, 'name', i18n.language)

  // Languages — API: languages[].dialect.language_name, a free-text field with no
  // per-locale translation. Map the common known names to Arabic; anything not in
  // the dictionary just falls back to the raw (English) name.
  const LANGUAGE_NAME_AR: Record<string, string> = {
    english: 'الإنجليزية', arabic: 'العربية', urdu: 'الأردية', hindi: 'الهندية',
    malayalam: 'المالايالامية', tagalog: 'التاغالوغية', bengali: 'البنغالية',
    french: 'الفرنسية', pashto: 'الباشتوية', punjabi: 'البنجابية', tamil: 'التاميلية',
    nepali: 'النيبالية', sinhala: 'السنهالية', farsi: 'الفارسية', persian: 'الفارسية',
    filipino: 'الفلبينية',
  }
  const languages = (user.languages ?? [])
    .map((l: any) => l.dialect?.language_name ?? l.language_name ?? l.name ?? '')
    .filter(Boolean)
    .map((name: string) => i18n.language === 'ar' ? (LANGUAGE_NAME_AR[name.trim().toLowerCase()] ?? name) : name)
    .join(', ') || '—'

  // Rating — API: flat rating field (number)
  const ratingRaw = user.rating ?? user.avg_rating
  const ratingDisplay = (ratingRaw != null && ratingRaw !== 0) ? Number(ratingRaw).toFixed(1) : '—'

  // Stats — all flat fields from API
  const activeStudents = user.total_active_students ?? 0
  const totalBookings  = user.total_bookings ?? 0
  // get-school-trainer-list has no revenue/earning fields — show cash-in-hand /
  // pending-online-payment instead, which the API actually returns for school trainers.
  const totalRevenue   = user.total_revenue ?? 0
  const trainerEarning = user.trainer_earning ?? 0
  const cashInHand      = user.cash_in_hand ?? 0
  const pendingOnlinePay = user.pending_online_payment ?? 0

  const statusStyleMap: Record<string, string> = {
    Active:    'bg-green-500 text-white',
    Inactive:  'bg-red-100 text-red-600',
    Suspended: 'bg-red-600 text-white',
  }
  const cardBgMap: Record<string, string> = {
    Active:    '#f0fdf4',
    Inactive:  '#fff5f5',
    Suspended: '#fff1f2',
  }
  const cardBg = cardBgMap[trainerStatus] ?? '#f9fafb'

  return (
    <div className={cn('relative bg-white rounded-2xl border shadow-sm hover:shadow-md transition-shadow overflow-hidden',
      selectable && selected ? 'ring-2 ring-brand-500 border-brand-500' : 'border-gray-100')}>
      {selectable && (
        <button type="button" onClick={onToggleSelect}
          aria-label={selected ? 'Deselect trainer' : 'Select trainer'}
          className={cn('absolute top-3 start-3 z-10 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors shadow-sm',
            selected ? 'bg-brand-500 border-brand-500 text-white' : 'bg-white/95 border-gray-300 hover:border-brand-400')}>
          {selected && <BadgeCheck size={12} strokeWidth={3} />}
        </button>
      )}

      {/* ── Header ── */}
      <div className="p-4 flex items-start justify-between gap-3" style={{ background: cardBg }}>
        <div className={cn('flex items-center gap-3 min-w-0', selectable && 'ps-6')}>
          <img src={user.photo_url || DUMMY_AVATAR} alt={trainerName} onError={onAvatarError}
            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100"/>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 text-sm leading-tight">{trainerName}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-gray-400">{t('trainerCard.idPrefix')}{user.id?.toString().padStart(4,'0')}</span>
              {isVerified && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[12px] font-semibold">
                  <BadgeCheck size={10}/> {t('trainerCard.verified')}
                </span>
              )}
            </div>
          </div>
        </div>
        {!deleted && (
          <span className={cn('px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 flex-shrink-0',
            statusStyleMap[trainerStatus] ?? 'bg-gray-100 text-gray-600')}>
            {trainerStatus === 'Suspended' && <span>⊘</span>}
            {statusLabel}
          </span>
        )}
      </div>

      {/* ── Contact info ── */}
      <div className="px-4 py-3 space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
            <Mail size={11} className="flex-shrink-0 text-gray-400"/>
            <span className="truncate">{user.email || '—'}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
            <Phone size={11} className="text-gray-400"/>
            <span>{user.phone ? `+${user.country_code ?? ''} ${user.phone}` : '—'}</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar size={11} className="flex-shrink-0 text-gray-400"/>
            <span>{t('trainerCard.joined')}: {formatDateTime(user.created_at)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <MapPin size={11} className="text-gray-400"/>
            <span>{cityName}</span>
          </div>
        </div>
        {deleted && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar size={11} className="flex-shrink-0 text-gray-400"/>
            <span>{t('trainerCard.deletedAt')}: {formatDateTime(user.deleted_at)}</span>
          </div>
        )}
      </div>

      {/* ── Key Info ── */}
      <div className="mx-4 mb-3 rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50 border-b border-gray-100">
          {t('trainerCard.keyInfo')}
        </div>
        <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
          {/* Vehicle */}
          <div className="flex items-center gap-2">
            <span className="text-gray-400 flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v5a2 2 0 01-2 2h-2m-8 0a2 2 0 104 0 2 2 0 00-4 0zm8 0a2 2 0 104 0 2 2 0 00-4 0z"/></svg>
            </span>
            <div>
              <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{t('trainerCard.vehicle')}</div>
              <div className="text-xs font-semibold text-gray-800">{vehicle}</div>
            </div>
          </div>
          {/* Gear */}
          <div className="flex items-center gap-2">
            <span className="text-gray-400 flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>
            </span>
            <div>
              <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{t('trainerCard.gear')}</div>
              <div className="text-xs font-semibold text-gray-800 capitalize">{gear}</div>
            </div>
          </div>
          {/* Plate Number */}
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-black text-gray-400 w-4 text-center flex-shrink-0">123</span>
            <div>
              <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{t('trainerCard.plateNumber')}</div>
              <div className="text-xs font-semibold text-gray-800">{plateNo}</div>
            </div>
          </div>
          {/* School */}
          <div className="flex items-center gap-2">
            <span className="text-gray-400 flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/></svg>
            </span>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{t('trainerCard.school')}</div>
              <div className="text-xs font-semibold text-gray-800 truncate">{school}</div>
            </div>
          </div>
          {/* Language */}
          <div className="flex items-center gap-2 col-span-1">
            <span className="text-gray-400 flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
            </span>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{t('trainerCard.language')}</div>
              <div className="text-xs font-semibold text-gray-800 truncate">{languages}</div>
            </div>
          </div>
          {/* Rating */}
          <div className="flex items-center gap-2">
            <span className="text-brand-400 flex-shrink-0">★</span>
            <div>
              <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">{t('trainerCard.rating')}</div>
              <div className="text-xs font-semibold text-gray-800">{ratingDisplay}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="mx-4 mb-3 grid grid-cols-2 divide-x divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
        <div className="py-2.5 text-center bg-brand-50">
          <div className="text-xl font-black text-gray-900">{activeStudents}</div>
          <div className="text-[12px] text-gray-500 font-medium mt-0.5">{t('trainerCard.activeStudents')}</div>
        </div>
        <div className="py-2.5 text-center bg-green-50">
          <div className="text-xl font-black text-gray-900">{totalBookings}</div>
          <div className="text-[12px] text-gray-500 font-medium mt-0.5">{t('trainerCard.totalBookings')}</div>
        </div>
      </div>

      {/* ── Revenue & Earning ── */}
      <div className="mx-4 mb-4 grid grid-cols-2 divide-x divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
        {isSchool ? (
          <>
            <div className="py-2.5 text-center bg-brand-50">
              <div className="text-xs font-black text-gray-900">{t('trainerCard.currency')} {Number(cashInHand).toLocaleString('en', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
              <div className="text-[12px] text-gray-500 font-medium mt-0.5">{t('trainerCard.cashInHand')}</div>
            </div>
            <div className="py-2.5 text-center bg-purple-50">
              <div className="text-xs font-black text-gray-900">{t('trainerCard.currency')} {Number(pendingOnlinePay).toLocaleString('en', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
              <div className="text-[12px] text-gray-500 font-medium mt-0.5">{t('trainerCard.pendingOnlinePayment')}</div>
            </div>
          </>
        ) : (
          <>
            <div className="py-2.5 text-center bg-brand-50">
              <div className="text-xs font-black text-gray-900">{t('trainerCard.currency')} {Number(totalRevenue).toLocaleString('en', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
              <div className="text-[12px] text-gray-500 font-medium mt-0.5">{t('trainerCard.totalRevenue')}</div>
            </div>
            <div className="py-2.5 text-center bg-purple-50">
              <div className="text-xs font-black text-gray-900">{t('trainerCard.currency')} {Number(trainerEarning).toLocaleString('en', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
              <div className="text-[12px] text-gray-500 font-medium mt-0.5">{t('trainerCard.trainerEarning')}</div>
            </div>
          </>
        )}
      </div>

      {/* ── Actions ── */}
      <div className="px-4 pb-4 flex items-center gap-2">
        <button
          onClick={onView}
          className="flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all hover:opacity-80"
          style={{ borderColor: '#f68b2c', color: '#f68b2c' }}
        >
          ● {t('trainerCard.viewDetails')}
        </button>
        {deleted ? (
          <button
            onClick={onRestore}
            disabled={restoring}
            title={t('trainerCard.restoreTitle')}
            className="flex items-center justify-center gap-1 py-2 px-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 bg-green-600 text-white hover:bg-green-700"
          >
            {restoring ? <Spinner className="w-4 h-4"/> : <RotateCcw size={16}/>}
            {t('trainerCard.restore')}
          </button>
        ) : (
          <>
            {!isVerified && onToggleVerify && (
              <button
                onClick={() => onToggleVerify(true)}
                disabled={verifying}
                title={t('trainerCard.verifyTitle')}
                className="flex items-center justify-center gap-1 py-2 px-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 bg-green-600 text-white hover:bg-green-700"
              >
                {verifying ? <Spinner className="w-4 h-4"/> : <BadgeCheck size={16}/>}
                {t('trainerCard.verify')}
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                disabled={deleting}
                title={t('trainerCard.deleteTitle')}
                className="flex items-center justify-center py-2 px-3 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {deleting ? <Spinner className="w-4 h-4"/> : <Trash2 size={16}/>}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
